import { useMemo, useState } from 'react';
import useProfileCommonStore from '../use-profile-common-store';
import { FUND_TYPES } from '../get-fund-type';
import { ASSET_CLASSES } from '../get-asset-class';
import DISCLOSURES from '../../../helpers/statics/performance-returns-disclosures';
import _ from 'lodash';
import format from '../../../utils/format';

import {
  useChartPerformanceReturns,
  useProfilePerformanceReturns,
  useProfileInvestmentFacts
} from '../../../hooks/api';

const PERIODS = [
  { label: 'Monthly', value: 'M' },
  { label: 'Quarterly', value: 'Q' }
];

const performanceDisclosureByFundType = {
  [FUND_TYPES.afterTaxAnnuity]: DISCLOSURES.afterTaxAnnuity,
  [FUND_TYPES.fixedAnnuityTiaa]: DISCLOSURES.performanceFixedAnnuityTiaa,
  [FUND_TYPES.variableAnnuityCref]: DISCLOSURES.performanceVariableAnnuityCref
};

const parseSupplementalData = supplementalData => {
  let supplementalDataParsed;

  try {
    supplementalDataParsed = JSON.parse(supplementalData);
  } catch (e) {
    supplementalDataParsed = [];
  }

  return supplementalDataParsed.reduce(
    (result, datum) => {
      const isMorningstarBenchmark =
        datum.VehicleType === 'Morningstar Secondary Benchmark';

      result.supplementalData.push({
        ...datum,
        isMorningstarBenchmark
      });

      result.showComparativeBenchmarkFootnote =
        result.showComparativeBenchmarkFootnote || isMorningstarBenchmark;

      return result;
    },
    {
      showComparativeBenchmarkFootnote: false,
      supplementalData: []
    }
  );
};

const getAlerternateUnderlyingInceptionDate = source => {
  return source.isLoaded ? source.data.underlyingFundInceptionDate : undefined;
};

const getPerformanceAsOfDate = (dateSource, fundType, isProprietaryFund, supplementalData = []) => {
  if (supplementalData.length > 0) {
    let perfAsOfDate;
    if (fundType === FUND_TYPES.mutualFund && !isProprietaryFund) {
      perfAsOfDate = _.last(supplementalData).AsOfDate;
    } else {
      perfAsOfDate = supplementalData[0].AsOfDate;
    }
    return format.oaDate(perfAsOfDate, {
      formatString: 'YYYY-MM-DD' // Since this get re-parsed, return as an ISO date.
    });
  }

  if (dateSource.isLoaded) {
    return dateSource.data.performanceAsOfDate;
  }

  return '';
};

const usePerformanceReturns = (
  isAlternateInceptionDate = false,
  showBenchmarkAndAverage = true
) => {
  const {
    apiConfigBase,
    commonState: {assetClass, fundType, isProprietaryFund }
  } = useProfileCommonStore();

  const { client, apiType, xid } = apiConfigBase;
  const [period, setPeriod] = useState(PERIODS[1]);
  const [chartWidth, setChartWidth] = useState();

  const handleChartResize = width => {
    setChartWidth(width);
  };

  const chartInputs = {
    apiType,
    client,
    shouldLoad: !!chartWidth, // don't make chart call until the container has a width
    params: {
      colorBenchmark: '#5A8010',
      colorCategoryAverage: '#512D6E',
      colorCompositeIndex: '#7F3785',
      colorGraphLines: '#666666',
      colorGraphText: '#666666',
      colorInvestment: '#0053A3',
      height: 300,
      period: period.value,
      showBenchmark: showBenchmarkAndAverage,
      showCategory: showBenchmarkAndAverage,
      showTimeframes: true,
      superScriptMarkers: {},
      timeframes: ['1m', '3m', 'ytd', '1y', '3y', '5y', '10y', 'si'],
      width: chartWidth >= 720 ? chartWidth : 720,
      xid
    }
  };

  const { data: chartData = {} } = useChartPerformanceReturns(chartInputs);

  const showUnderlyingFundInceptionDate =
    fundType === FUND_TYPES.variableAnnuityTiaaAccess || fundType === FUND_TYPES.afterTaxAnnuity;

  const showExpenseRatio = fundType === FUND_TYPES.afterTaxAnnuity;

  const showPortfolioInceptionDate =
    [FUND_TYPES.variableAnnuityCref, FUND_TYPES.variableAnnuityTiaa].indexOf(fundType) !== -1
    || (fundType === FUND_TYPES.mutualFund && isProprietaryFund);

  const dateSource = useProfilePerformanceReturns({
    ...apiConfigBase,
    shouldLoad: isAlternateInceptionDate && showUnderlyingFundInceptionDate
  });
  const underlyingFundInceptionDate =
    isAlternateInceptionDate && dateSource.isLoaded
      ? getAlerternateUnderlyingInceptionDate(dateSource)
      : chartData.underlyingFundInceptionDate;

  const { supplementalData, showComparativeBenchmarkFootnote } = useMemo(
    () => parseSupplementalData(chartData.supplementalData),
    [chartData.supplementalData]
  );

  const showEstimatedExpenseCharge =
    fundType === FUND_TYPES.variableAnnuityCref ||
    assetClass === ASSET_CLASSES.realEstate;

  const performanceAsOfDate = getPerformanceAsOfDate(
    dateSource,
    fundType,
    isProprietaryFund,
    supplementalData
  );

  return {
    fundData: supplementalData[0] || {},
    onChangePeriod: setPeriod,
    onResizeChart: handleChartResize,
    periods: PERIODS,
    periodSelected: period,
    performanceAsOfDate,
    performanceDisclosure:
      performanceDisclosureByFundType[fundType] ||
      performanceDisclosureByFundType[FUND_TYPES.fixedAnnuityTiaa],
    showComparativeBenchmarkFootnote,
    showExpenseRatio,
    showEstimatedExpenseCharge,
    showPortfolioInceptionDate: showPortfolioInceptionDate || false,
    showUnderlyingFundInceptionDate,
    src: chartData.image ? chartData.image : undefined,
    supplementalData,
    underlyingFundInceptionDate,
    fundType,
    investmentFacts: useProfileInvestmentFacts(apiConfigBase)
  };
};

export default usePerformanceReturns;
